import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { useUser } from '../../components/UserContext.tsx';
import CircleStatus from '../../recovery/CircleStatus.tsx';
import KeyholderAssignments from '../../recovery/KeyholderAssignments.tsx';
import OwnerCircleSetup from '../../recovery/OwnerCircleSetup.tsx';
import RecoveryDevices from '../../recovery/RecoveryDevices.tsx';
import RecoveryLimitations from '../../recovery/RecoveryLimitations.tsx';
import RecoveryRequests, {
  RecoveryRequestList,
} from '../../recovery/RecoveryRequests.tsx';
import {
  recoveryApi,
  type RecoveryApiClient,
} from '../../recovery/api.ts';
import {
  assertEncryptedManifest,
  assertEncryptedShareEnvelope,
  assertRecoveryVaultEnvelope,
} from '../../recovery/payloadSafety.ts';
import {
  circleForRotation,
  circleStatusLabel,
  requestIsActive,
} from '../../recovery/recoveryUi.ts';
import type {
  ActivateCircleInput,
  CompleteRecoveryOnTarget,
  CreateCircleInput,
  CreateRecoveryRequestInput,
  DecideRecoveryRequestInput,
  GetDeviceCustodyPublicKey,
  GetMembershipCustodyPublicKey,
  GetRecoveryTargetPublicKey,
  GetRequestBoundSealedShare,
  PrepareCircleActivation,
  RecoveryCircle,
  RecoveryDevice,
  RecoveryInvitation,
  RecoveryOverview,
  RecoveryRequest,
  RecoveryUser,
  RecoveryUserId,
  LocalCryptoHealth,
  RegisterRecoveryDeviceInput,
  RespondToMembershipInput,
  RetireRecoveryCircle,
} from '../../recovery/types.ts';
import './RecoveryCenter.scss';

function tabTitle(label: string, count: number) {
  return (
    <span>
      {label}{' '}
      <Badge bg={count ? 'primary' : 'secondary'} pill>
        <span className="visually-hidden">{label}: </span>
        {count}
      </Badge>
    </span>
  );
}

export function validateActivationPayload(
  circle: RecoveryCircle,
  payload: ActivateCircleInput
): void {
  const manifest = assertEncryptedManifest(payload.manifest);
  const vaultEnvelope = assertRecoveryVaultEnvelope(payload.vaultEnvelope);
  if (
    manifest.circleId !== circle.id ||
    manifest.threshold !== circle.threshold ||
    manifest.total !== circle.memberCount ||
    (circle.version !== undefined && manifest.circleVersion !== circle.version)
  ) {
    throw new Error(
      'The encrypted activation manifest does not match this circle version, threshold, and membership count.'
    );
  }
  if (
    vaultEnvelope.circleId !== circle.id ||
    (circle.version !== undefined && vaultEnvelope.circleVersion !== circle.version)
  ) {
    throw new Error(
      'The recovery vault envelope is not bound to this circle version.'
    );
  }
  const membershipIds = circle.members.map(
    (member) => member.membershipId || member.id
  );
  if (payload.encryptedShares.length !== membershipIds.length) {
    throw new Error(
      'Device crypto must provide one encrypted envelope for every membership.'
    );
  }
  const suppliedIds = new Set<string>();
  for (const share of payload.encryptedShares) {
    if (
      !membershipIds.includes(share.membershipId) ||
      suppliedIds.has(share.membershipId)
    ) {
      throw new Error(
        'Device crypto returned missing, duplicate, or invalid membership envelopes.'
      );
    }
    const envelope = assertEncryptedShareEnvelope(share.envelope);
    if (
      envelope.context.purpose !== 'initial-share-custody' ||
      envelope.context.circleId !== circle.id ||
      envelope.context.membershipId !== share.membershipId ||
      (circle.version !== undefined &&
        envelope.context.circleVersion !== circle.version)
    ) {
      throw new Error(
        'An encrypted membership envelope is not bound to this circle and membership.'
      );
    }
    suppliedIds.add(share.membershipId);
  }
}

export interface RecoveryCenterProps {
  apiClient?: RecoveryApiClient;
  users?: RecoveryUser[];
  currentUserId?: RecoveryUserId;
  prepareCircleActivation?: PrepareCircleActivation;
  getMembershipCustodyPublicKeyJwk?: GetMembershipCustodyPublicKey;
  getDeviceCustodyPublicKeyJwk?: GetDeviceCustodyPublicKey;
  getRecoveryTargetPublicKeyJwk?: GetRecoveryTargetPublicKey;
  getRequestBoundSealedShare?: GetRequestBoundSealedShare;
  completeRecoveryOnTarget?: CompleteRecoveryOnTarget;
  retireCircle?: RetireRecoveryCircle;
  onDeviceRevoked?: (device: RecoveryDevice) => Promise<void>;
  localCryptoHealth?: LocalCryptoHealth;
  chatIdentityUnlocked?: boolean;
}

export default function RecoveryCenter({
  apiClient = recoveryApi,
  users,
  currentUserId,
  prepareCircleActivation,
  getMembershipCustodyPublicKeyJwk,
  getDeviceCustodyPublicKeyJwk,
  getRecoveryTargetPublicKeyJwk,
  getRequestBoundSealedShare,
  completeRecoveryOnTarget,
  retireCircle,
  onDeviceRevoked,
  localCryptoHealth,
  chatIdentityUnlocked,
}: RecoveryCenterProps) {
  const { activeUser, users: contextUsers } = useUser();
  const effectiveUserId = currentUserId ?? activeUser?.id;
  const availableUsers =
    users ?? (contextUsers.length ? contextUsers : undefined);
  const [overview, setOverview] = useState<RecoveryOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<string | null>(null);

  const loadOverview = useCallback(
    async (signal?: AbortSignal, initial = false) => {
      if (initial) setLoading(true);
      else setRefreshing(true);
      try {
        const result = await apiClient.getOverview(signal);
        setOverview(result);
        setPageError(null);
      } catch (cause) {
        if (cause instanceof Error && cause.name === 'AbortError') return;
        setPageError(
          cause instanceof Error
            ? cause.message
            : 'Could not load the recovery center.'
        );
      } finally {
        if (initial) setLoading(false);
        else setRefreshing(false);
      }
    },
    [apiClient]
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadOverview(controller.signal, true);
    return () => controller.abort();
  }, [loadOverview]);

  const refreshAfter = useCallback(
    async (action: () => Promise<void>, successMessage: string) => {
      await action();
      setAnnouncement(successMessage);
      await loadOverview();
    },
    [loadOverview]
  );

  const loadUsers = useCallback(() => apiClient.getUsers(), [apiClient]);

  const handleCreateCircle = (input: CreateCircleInput) =>
    refreshAfter(
      () => apiClient.createCircle(input),
      'Recovery-circle invitations were created.'
    );

  const handleActivation = async (circle: RecoveryCircle) => {
    if (!prepareCircleActivation) {
      throw new Error(
        'Encrypted activation is unavailable until parent device crypto is connected.'
      );
    }
    const payload = await prepareCircleActivation(circle);
    if (!payload) {
      throw new Error(
        'Device crypto did not produce activation data. Nothing was sent.'
      );
    }
    validateActivationPayload(circle, payload);
    await refreshAfter(
      () => apiClient.activateCircle(circle.id, payload),
      'The recovery circle was activated.'
    );
  };

  const handleRotation = async (circle: RecoveryCircle) => {
    if (!prepareCircleActivation) {
      throw new Error(
        'Rotation is unavailable until parent device crypto is connected.'
      );
    }
    const next = circleForRotation(circle);
    const payload = await prepareCircleActivation(next);
    if (!payload) {
      throw new Error(
        'Device crypto did not produce rotation data. Nothing was sent.'
      );
    }
    validateActivationPayload(next, payload);
    await refreshAfter(
      () => apiClient.rotateCircle(circle.id, payload),
      `The recovery circle was rotated to version ${next.version}. Same keyholders keep custody if this browser still has their keys.`
    );
  };

  const handleMembershipResponse = (
    invitation: RecoveryInvitation,
    input: RespondToMembershipInput
  ) =>
    refreshAfter(
      () => apiClient.respondToMembership(invitation.id, input),
      input.decision === 'accept'
        ? 'The keyholder role was accepted on this device.'
        : 'The keyholder invitation was declined.'
    );

  const handleRegisterDevice = (input: RegisterRecoveryDeviceInput) =>
    refreshAfter(
      () => apiClient.registerDevice(input),
      'The recovery device was registered.'
    );

  const handleRevokeDevice = (device: RecoveryDevice) =>
    refreshAfter(async () => {
      await apiClient.revokeDevice(device.id);
      if (onDeviceRevoked) await onDeviceRevoked(device);
    }, `${device.name} was revoked.`);

  const handleCreateRequest = (input: CreateRecoveryRequestInput) =>
    refreshAfter(
      () => apiClient.createRequest(input),
      'The authenticated recovery drill was created.'
    );

  const handleDecideRequest = (
    request: RecoveryRequest,
    input: DecideRecoveryRequestInput
  ) =>
    refreshAfter(
      () => apiClient.decideRequest(request.id, input),
      `Your ${input.decision} decision was recorded.`
    );

  const handleCancelRequest = (request: RecoveryRequest) =>
    refreshAfter(
      () => apiClient.cancelRequest(request.id),
      'The recovery drill was cancelled.'
    );

  const handleRetireCircle = (circle: RecoveryCircle) => {
    if (!retireCircle) {
      return Promise.reject(
        new Error('Retiring drill material is unavailable until the retire callback is connected.')
      );
    }
    return refreshAfter(
      () => retireCircle(circle),
      'The circle was retired. Create a new one only if you do not want to keep the same keyholders.'
    );
  };

  const handleCompleteRequest = (request: RecoveryRequest) => {
    if (!completeRecoveryOnTarget) {
      return Promise.reject(
        new Error(
          'Completion is unavailable until parent target-device crypto is connected.'
        )
      );
    }
    return refreshAfter(async () => {
      const requestDetails = await apiClient.getRequest(request.id);
      await completeRecoveryOnTarget(request, requestDetails);
      await apiClient.completeRequest(request.id);
      if (!prepareCircleActivation) {
        throw new Error(
          'Adult-chat keys were installed. Rotation is still required, but device crypto is not connected.'
        );
      }
      const overviewAfter = await apiClient.getOverview();
      const circle = overviewAfter.ownedCircle;
      if (!circle?.rotationRequired) {
        return;
      }
      const next = circleForRotation(circle);
      const payload = await prepareCircleActivation(next);
      if (!payload) {
        throw new Error(
          'Adult-chat keys were installed. Rotation still needs encrypted envelopes from this device.'
        );
      }
      validateActivationPayload(next, payload);
      await apiClient.rotateCircle(circle.id, payload);
    }, 'Adult-chat keys were installed and the recovery circle was rotated to a new version.');
  };

  const counts = useMemo(() => {
    if (!overview) {
      return { owned: 0, supporting: 0, requests: 0, devices: 0 };
    }
    return {
      owned: overview.ownedCircle ? 1 : 0,
      supporting: overview.invitations.length + overview.assignments.length,
      requests: overview.requests.filter(requestIsActive).length,
      devices: overview.devices.length,
    };
  }, [overview]);

  if (loading) {
    return (
      <div className="main-content recovery-center">
        <Container className="my-4">
          <div className="recovery-page-state" role="status" aria-live="polite">
            <Spinner animation="border" aria-hidden="true" />
            <p>Loading recovery-circle status…</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="main-content recovery-center">
        <Container className="my-4">
          <h1 className="display-6">Recovery circle</h1>
          <Alert variant="danger" role="alert">
            {pageError || 'The recovery center is unavailable.'}
          </Alert>
          <Button onClick={() => loadOverview()} disabled={refreshing}>
            {refreshing ? 'Retrying…' : 'Retry'}
          </Button>
        </Container>
      </div>
    );
  }

  const activeRequests = overview.requests.filter(requestIsActive);
  const history = overview.requests.filter(
    (request) => !requestIsActive(request)
  );

  return (
    <div className="main-content recovery-center">
      <Container className="my-4 mb-5">
        <header className="recovery-header">
          <div>
            <h1 className="display-6 mb-2">Recovery circle</h1>
            <p className="lead mb-0">
              Personal adult keyholders and authenticated recovery drills.
            </p>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={refreshing}
            onClick={() => loadOverview()}
          >
            {refreshing ? 'Refreshing…' : 'Refresh status'}
          </Button>
        </header>

        {localCryptoHealth && (
          <Alert
            variant={localCryptoHealth.supported ? 'info' : 'danger'}
            className="mt-3"
          >
            <strong>Local crypto: </strong>
            {localCryptoHealth.message}
            {localCryptoHealth.missingMembershipIds.length > 0 && (
              <span className="d-block mt-2">
                This profile is missing a custody key for one or more accepted
                assignments. Another browser profile will not have those keys,
                and they will not be regenerated silently.
              </span>
            )}
            {localCryptoHealth.missingDeviceFingerprints.length > 0 && (
              <span className="d-block mt-2">
                A registered device fingerprint has no matching local key in
                this browser profile.
              </span>
            )}
          </Alert>
        )}

        {chatIdentityUnlocked === false && (
          <Alert variant="warning" className="mt-3">
            Unlock adult-chat keys with your login password (Settings) before
            activating or completing recovery. A dummy vault key will not be
            created.
          </Alert>
        )}

        <Alert variant="warning" className="mt-3">
          Institution custody, lost-device proof without a login, and
          incapacity recovery are not available in this slice. Completing
          lockout installs adult-chat keys and rotates the circle to a new
          version for the same keyholders. It still needs a signed-in session
          on the named device.
        </Alert>

        <div className="visually-hidden" aria-live="polite" aria-atomic="true">
          {refreshing ? 'Refreshing recovery status.' : announcement}
        </div>
        {pageError && (
          <Alert variant="danger" aria-live="assertive">
            Status refresh failed: {pageError}
          </Alert>
        )}

        <section aria-labelledby="recovery-summary-heading">
          <h2 id="recovery-summary-heading" className="h4">
            Status summary
          </h2>
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title as="h3" className="h6">
                    My circle
                  </Card.Title>
                  <p className="h5 mb-0">
                    {overview.ownedCircle
                      ? overview.ownedCircle.rotationRequired
                        ? 'Rotation required'
                        : circleStatusLabel(overview.ownedCircle.status)
                      : 'Not created'}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title as="h3" className="h6">
                    People I support
                  </Card.Title>
                  <p className="h5 mb-0">
                    {overview.assignments.length} active ·{' '}
                    {overview.invitations.length} invited
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title as="h3" className="h6">
                    Open requests
                  </Card.Title>
                  <p className="h5 mb-0">{activeRequests.length}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title as="h3" className="h6">
                    Registered devices
                  </Card.Title>
                  <p className="h5 mb-0">{overview.devices.length}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        <Tabs
          defaultActiveKey="circle"
          id="recovery-center-sections"
          className="recovery-tabs mb-4"
          mountOnEnter
        >
          <Tab eventKey="circle" title={tabTitle('My recovery circle', counts.owned)}>
            <section aria-labelledby="my-recovery-circle-heading">
              <h2 id="my-recovery-circle-heading" className="h3">
                My recovery circle
              </h2>
              {overview.ownedCircle ? (
                <CircleStatus
                  circle={overview.ownedCircle}
                  onActivate={
                    prepareCircleActivation ? handleActivation : undefined
                  }
                  onRotate={
                    prepareCircleActivation ? handleRotation : undefined
                  }
                  onRetire={retireCircle ? handleRetireCircle : undefined}
                />
              ) : (
                <>
                  <p className="recovery-empty">
                    You do not have a recovery circle yet. Start with personal
                    adult keyholders.
                  </p>
                  {effectiveUserId === undefined ? (
                    <Alert variant="warning">
                      Circle setup is unavailable until the signed-in user
                      identity is loaded, because the owner must be excluded
                      from keyholder choices.
                    </Alert>
                  ) : (
                    <OwnerCircleSetup
                      users={availableUsers}
                      currentUserId={effectiveUserId}
                      loadUsers={loadUsers}
                      onCreate={handleCreateCircle}
                    />
                  )}
                </>
              )}
            </section>
          </Tab>

          <Tab
            eventKey="support"
            title={tabTitle('People I support', counts.supporting)}
          >
            <KeyholderAssignments
              invitations={overview.invitations}
              assignments={overview.assignments}
              getCustodyPublicKeyJwk={getMembershipCustodyPublicKeyJwk}
              onRespond={handleMembershipResponse}
            />
          </Tab>

          <Tab
            eventKey="requests"
            title={tabTitle('Requests', counts.requests)}
          >
            <RecoveryRequests
              circle={overview.ownedCircle}
              devices={overview.devices}
              requests={overview.requests}
              getTargetPublicKeyJwk={getRecoveryTargetPublicKeyJwk}
              getRequestBoundSealedShare={getRequestBoundSealedShare}
              onCreate={handleCreateRequest}
              onDecide={handleDecideRequest}
              onCancel={handleCancelRequest}
              onComplete={
                completeRecoveryOnTarget
                  ? handleCompleteRequest
                  : undefined
              }
            />
          </Tab>

          <Tab
            eventKey="devices"
            title={tabTitle('Devices & alerts', counts.devices)}
          >
            <RecoveryDevices
              devices={overview.devices}
              getCustodyPublicKeyJwk={getDeviceCustodyPublicKeyJwk}
              onRegister={handleRegisterDevice}
              onRevoke={handleRevokeDevice}
            />
          </Tab>

          <Tab
            eventKey="history"
            title={tabTitle('History & limitations', history.length)}
          >
            <section aria-labelledby="recovery-history-heading">
              <h2 id="recovery-history-heading" className="h3">
                Request history
              </h2>
              <RecoveryRequestList
                requests={history}
                emptyMessage="No completed, cancelled, denied, reported, or expired requests."
                getRequestBoundSealedShare={getRequestBoundSealedShare}
                onDecide={handleDecideRequest}
                onCancel={handleCancelRequest}
                onComplete={
                  completeRecoveryOnTarget
                    ? handleCompleteRequest
                    : undefined
                }
              />
            </section>
            <RecoveryLimitations />
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
}
